import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

const C = {
  navy: '#122326', slate: '#213A3E', gold: '#BF6847',
  cream: '#F0EFEB', warm: '#F7F6F3', sand: '#E2E0DB',
  red: '#c0392b', green: '#27ae60',
};
const FF = {
  display: "'Libre Baskerville', Georgia, serif",
  body: "'Nunito', 'Segoe UI', sans-serif",
};

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn, signUp } = useAuth();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get('mode') === 'signup' ? 'signup' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [municipalityId, setMunicipalityId] = useState('');
  const [municipalities, setMunicipalities] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function fetchMunicipalities() {
      const { data } = await supabase
        .from('municipalities')
        .select('id, name, county')
        .order('county', { ascending: true })
        .order('name', { ascending: true });
      if (data) setMunicipalities(data);
    }
    fetchMunicipalities();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      if (mode === 'login') {
        const { error: err } = await signIn(email, password);
        if (err) { setError(err.message); setSubmitting(false); return; }
        navigate('/dashboard');
      } else {
        if (!fullName.trim()) { setError('Please enter your full name.'); setSubmitting(false); return; }
        const { error: err } = await signUp(email, password, fullName, municipalityId || null, 'clerk');
        if (err) { setError(err.message); setSubmitting(false); return; }
        setSuccess('Account created! Check your email to confirm, then log in.');
        setMode('login');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  }

  // Group municipalities by county for the dropdown
  const grouped = municipalities.reduce((acc, m) => {
    if (!acc[m.county]) acc[m.county] = [];
    acc[m.county].push(m);
    return acc;
  }, {});

  const inputStyle = {
    width: '100%', padding: '11px 14px', borderRadius: 8,
    border: `1.5px solid ${C.sand}`, fontSize: 14, fontFamily: FF.body,
    boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.2s',
    background: '#fff',
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `linear-gradient(160deg, ${C.navy} 0%, ${C.slate} 50%, #2a5558 100%)`,
      fontFamily: FF.body, padding: '24px',
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=Nunito:wght@400;600;700&display=swap" rel="stylesheet" />

      <div style={{
        width: '100%', maxWidth: 420, background: '#fff', borderRadius: 16,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          background: C.navy, padding: '28px 32px', textAlign: 'center',
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: C.gold, textTransform: 'uppercase',
            letterSpacing: '0.15em', marginBottom: 8,
          }}>
            Panhandle Area Development District
          </div>
          <div style={{
            fontFamily: FF.display, fontSize: 22, fontWeight: 700, color: '#fff',
          }}>
            CDBG Hub
          </div>
        </div>

        {/* Tab toggle */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.sand}` }}>
          {['login', 'signup'].map((m) => (
            <button key={m} onClick={() => { setMode(m); setError(''); setSuccess(''); }}
              style={{
                flex: 1, padding: '14px', border: 'none', cursor: 'pointer',
                fontSize: 14, fontWeight: 600, fontFamily: FF.body,
                background: mode === m ? '#fff' : C.cream,
                color: mode === m ? C.navy : C.slate,
                borderBottom: mode === m ? `3px solid ${C.gold}` : '3px solid transparent',
                transition: 'all 0.2s',
              }}>
              {m === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '28px 32px' }}>
          {mode === 'signup' && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.navy, marginBottom: 5 }}>
                Full Name
              </label>
              <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
                placeholder="Jane Doe" required style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = C.gold)}
                onBlur={(e) => (e.target.style.borderColor = C.sand)} />
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.navy, marginBottom: 5 }}>
              Email
            </label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" required style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = C.gold)}
              onBlur={(e) => (e.target.style.borderColor = C.sand)} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.navy, marginBottom: 5 }}>
              Password
            </label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" required minLength={6} style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = C.gold)}
              onBlur={(e) => (e.target.style.borderColor = C.sand)} />
          </div>

          {mode === 'signup' && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.navy, marginBottom: 5 }}>
                Municipality <span style={{ fontWeight: 400, color: C.slate }}>(optional)</span>
              </label>
              <select value={municipalityId} onChange={(e) => setMunicipalityId(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">— Select your municipality —</option>
                {Object.entries(grouped).map(([county, munis]) => (
                  <optgroup key={county} label={`${county} County`}>
                    {munis.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          )}

          {error && (
            <div style={{
              background: '#fdf0ed', border: `1px solid ${C.red}`, borderRadius: 8,
              padding: '10px 14px', marginBottom: 16, fontSize: 13, color: C.red,
            }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{
              background: '#eafaf1', border: `1px solid ${C.green}`, borderRadius: 8,
              padding: '10px 14px', marginBottom: 16, fontSize: 13, color: C.green,
            }}>
              {success}
            </div>
          )}

          <button type="submit" disabled={submitting}
            style={{
              width: '100%', padding: '13px', border: 'none', borderRadius: 8,
              background: submitting ? C.sand : `linear-gradient(135deg, ${C.gold}, #a0553a)`,
              color: '#fff', fontSize: 15, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
              fontFamily: FF.body, transition: 'all 0.2s',
              boxShadow: submitting ? 'none' : '0 4px 16px rgba(191,104,71,0.3)',
            }}>
            {submitting ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>

          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Link to="/" style={{
              fontSize: 13, color: C.slate, textDecoration: 'none',
            }}>
              ← Back to Public Site
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
